import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getOverlayZIndex } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { useElevation, useNearestSurface, } from '#pplx/frontend/aether/components/Surface/useSurface.js';
import { useTruncationAwareOpenState } from '#pplx/frontend/aether/components/Tooltip/internal/useTruncationAwareOpenState.js';
import { useModalityAwareOnOpenChange } from '#pplx/frontend/aether/components/Tooltip/useModalityAwareOnOpenChange.js';
import { useHideWhenDetached } from '#pplx/frontend/aether/internal/floating-ui/useHideWhenDetached.js';
import { useCollisionPaddingWithTopInset } from '#pplx/frontend/aether/internal/popover-collision/useCollisionPaddingWithTopInset.js';
import { useFloatingAnchorSentinel } from '#pplx/frontend/aether/internal/useFloatingAnchorSentinel.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import { Slot } from '@radix-ui/react-slot';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva } from 'class-variance-authority';
import { useMemo, useState } from 'react';
const DEFAULT_OFFSET_PX = 8;
const DEFAULT_MAX_WIDTH_PX = 280;
const COLLISION_PADDING = 10;
const tooltipContentVariants = cva([
    // Background and border. The shadow token draws the keyline as an inset
    // shadow, so a regular border would show as a second ring outside it.
    // Only forced-colors mode, which drops shadows, gets a real border.
    'bg-dark forced-colors:border forced-colors:border-[CanvasText]',
    // Spacing
    'px-2 py-1.5',
    // Shape
    'rounded-lg',
    // Shadow
    'shadow-sm',
], {
    variants: {
        side: {
            top: 'data-[open-mode=delayed-open]:data-[state=closed]:animate-slideDownAndFadeOut data-[state=delayed-open]:animate-slideUpAndFadeIn',
            bottom: 'data-[open-mode=delayed-open]:data-[state=closed]:animate-slideUpAndFadeOut data-[state=delayed-open]:animate-slideDownAndFadeIn',
            left: 'data-[open-mode=delayed-open]:data-[state=closed]:animate-slideRightAndFadeOut data-[state=delayed-open]:animate-slideLeftAndFadeIn',
            right: 'data-[open-mode=delayed-open]:data-[state=closed]:animate-slideLeftAndFadeOut data-[state=delayed-open]:animate-slideRightAndFadeIn',
        },
    },
    defaultVariants: {
        side: 'top',
    },
});
function TooltipContentElement({ 'data-state': state, ...props }) {
    // Radix reports both open modes as `closed` during exit. Retain the last
    // open mode so only delayed tooltips receive an exit animation.
    const [lastOpenState, setLastOpenState] = useState(state);
    if (state !== 'closed' && state !== lastOpenState) {
        setLastOpenState(state);
    }
    return _jsx("div", { ...props, "data-state": state, "data-open-mode": lastOpenState });
}
/**
 * Hover tooltip with smart positioning, optional subtitle, and configurable
 * delay. The content prop must be a string.
 * Radix instant opens (including keyboard focus and provider skip-delay opens)
 * have no entrance or exit animation. Delayed opens retain both animations.
 *
 * While the tooltip content is mounted — open or playing its exit animation — a hidden
 * `data-state="open"` sentinel is rendered next to the trigger. Hover-revealed trigger containers
 * can rely on ancestor `:has([data-state=open])` styling (e.g.
 * `group-has-[[data-state=open]]/row:flex`) to keep the trigger's layout box until the tooltip has
 * fully closed, so an exiting tooltip stays anchored instead of jumping to the viewport origin.
 *
 * @example
 * ```tsx
 * <Tooltip content="Save changes">
 *   <Button icon={IconSave} aria-label="Save" />
 * </Tooltip>
 * ```
 *
 * @category components
 * @keywords tooltip, components, hover, with, smart, positioning, optional,
 *   subtitle, configurable, delay, content, prop, must, string, disabled,
 *   popover, toggle-tip, button, icon
 * @whenToUse Use Tooltip to display a brief, non-interactive text label when
 *   hovering over or focusing a UI element. Tooltips are ideal for naming
 *   icon-only buttons, describing truncated text, showing keyboard shortcuts,
 *   and providing supplementary context. The component supports configurable
 *   delay, positioning, and an optional subtitle for secondary information.
 *   For interactive content, use Popover; for click-to-reveal information,
 *   use ToggleTip.
 * @guidelineDo
 *   - Use Tooltip for short, non-interactive text labels that describe or name a UI element on
 *     hover
 *   - Use the subtitle prop for supplementary detail below the main content, such as a keyboard
 *     shortcut
 *   - Set shouldStayOpenOnClick to true for copy-to-clipboard patterns where the tooltip text
 *     changes to 'Copied' after clicking
 *   - Use the side and align props to position the tooltip away from interactive areas that might
 *     be obscured
 *   - Use shouldOpenOnlyWhenTruncated when the direct trigger element truncates or clamps its
 *     content
 * @guidelineDont
 *   - Don't put interactive content (buttons, links, forms) inside a Tooltip — use Popover or
 *     ToggleTip instead
 *   - Don't use Tooltip as the sole means of conveying critical information — tooltips are
 *     supplementary and may not be accessible to all users
 *   - Don't set delayDurationMs to 0 — a brief delay prevents tooltips from flashing during normal
 *     mouse movement
 *   - Don't use Tooltip on disabled elements directly — disabled elements are not focusable, so
 *     wrap in a span if needed
 *   - Don't override maxWidthPx to very large values — tooltips should be concise; use Popover for
 *     longer content
 * @accessibility
 *   - Provides the tooltip ARIA role and manages aria-describedby on the trigger element
 *   - Keyboard focus opens the tooltip instantly without animation
 *   - Escape key dismisses the tooltip, following the WAI-ARIA tooltip pattern
 *   - The tooltip content uses white text on a dark background with a minimum contrast ratio that
 *     meets WCAG AA standards
 *   - When disabled is true, the trigger renders without any tooltip wrapper, keeping the DOM
 *     clean and preventing empty tooltip announcements
 *   - When shouldOpenOnlyWhenTruncated is true, the direct trigger must forward a DOM ref so
 *     Tooltip can measure whether its content is clipped
 * @relatedComponents
 *   - popover
 *   - toggle-tip
 *   - button
 *   - icon
 */
export function Tooltip({ ref, content, subtitle, side = 'top', align = 'center', open, onOpenChange, children, shouldShowArrow = false, delayDurationMs = 700, skipDelayDurationMs, disabled = false, shouldOpenOnlyWhenTruncated = false, offsetPx = DEFAULT_OFFSET_PX, portalTargetElement, shouldStayOpenOnClick = false, maxWidthPx = DEFAULT_MAX_WIDTH_PX, ...props }) {
    const collisionPadding = useCollisionPaddingWithTopInset(COLLISION_PADDING);
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const surfacePortalTarget = useNearestSurface();
    const parentElevation = useElevation();
    const resolvedPortalTarget = shouldUseActivationOrder
        ? undefined
        : (portalTargetElement ?? surfacePortalTarget);
    // Apply overlay-tier z-index so the tooltip renders above sibling Surfaces.
    const overlayZIndexStyle = useMemo(() => shouldUseActivationOrder ? undefined : { zIndex: getOverlayZIndex(parentElevation) }, [parentElevation, shouldUseActivationOrder]);
    const targetRef = useMeasureableHandle(ref);
    const openStateOptions = open === undefined ? {} : { open, onOpenChange };
    const { isOpen, handleOpenChange } = useTruncationAwareOpenState({
        targetRef,
        ...openStateOptions,
        shouldOpenOnlyWhenTruncated,
        disabled,
    });
    // Keep a `data-state="open"` sentinel mounted next to the trigger while the
    // tooltip content exists — open or playing its exit animation. Hover-revealed
    // trigger containers shown via `:has([data-state=open])` keep their layout
    // box that way (the Radix tooltip trigger itself only reports
    // `delayed-open`/`instant-open`), so a tooltip whose trigger container hides
    // mid-exit stays anchored instead of re-measuring a boxless anchor as a 0x0
    // rect at the viewport origin and flashing there (DSYS-614).
    const { sentinel, contentMountSignal } = useFloatingAnchorSentinel();
    const hideWhenDetached = useHideWhenDetached();
    // Manually pick the props that Radix UI passes in for their own internal usage
    const __radixProps = useRadixProps(props);
    const { onPointerEnter: handlePointerEnter, onPointerLeave: handlePointerLeave, onOpenChange: modalityAwareHandleOpenChange, } = useModalityAwareOnOpenChange({ onOpenChange: handleOpenChange });
    // Radix rotates the arrow for each placement, keeping its bottom and right
    // edges facing away from the tooltip. The arrow reuses the tooltip's
    // `shadow-sm` so its keyline and shadow match the body's in both themes.
    // The clip keeps only the arrow's outer half (plus room for the shadow), so
    // nothing is drawn twice over the body.
    //
    // Where the arrow joins the body, the clip line moves into the body by
    // `--tooltip-arrow-join` so the arrow covers the body's edge line there:
    // nothing in light mode (its keyline sits outside the body), one hairline in
    // dark mode (its keyline is inset), and the 1px border in forced-colors mode
    // (which drops shadows and uses real borders instead). The shift is scaled
    // by 2.4142 (1 + the square root of 2) because the clip runs along the
    // square's diagonal and also skips the arrow's own inner edges, so their
    // inset keyline and highlight don't show at the corners.
    const arrowClasses = [
        'size-2 -translate-y-1 rotate-45 bg-dark shadow-sm',
        '[--tooltip-arrow-join:0px] dark:[--tooltip-arrow-join:var(--shadow-hairline-width)]',
        'forced-colors:[--tooltip-arrow-join:1px] dark:forced-colors:[--tooltip-arrow-join:1px]',
        '[clip-path:polygon(200%_-100%,calc(100%_-_var(--tooltip-arrow-join))_var(--tooltip-arrow-join),calc(100%_-_var(--tooltip-arrow-join)_*_2.4142)_var(--tooltip-arrow-join),var(--tooltip-arrow-join)_calc(100%_-_var(--tooltip-arrow-join)_*_2.4142),var(--tooltip-arrow-join)_calc(100%_-_var(--tooltip-arrow-join)),-100%_200%,200%_200%)]',
        'forced-colors:border-b forced-colors:border-r forced-colors:border-[CanvasText]',
    ].join(' ');
    const triggerChild = ref != null || shouldOpenOnlyWhenTruncated ? _jsx(Slot, { ref: targetRef, children: children }) : children;
    if (disabled) {
        // When disabled, we still need to forward any Radix props from parent
        // components (e.g. Popover.Trigger) so that event handlers like onClick
        // and onPointerDown reach the underlying DOM element.  Without this,
        // a controlled Popover whose trigger is a disabled Tooltip loses its
        // toggle-close behaviour because the trigger click handler is dropped.
        if (Object.keys(__radixProps).length > 0) {
            return _jsx(Slot, { ...__radixProps, children: triggerChild });
        }
        return triggerChild;
    }
    const tooltipNode = (_jsxs(TooltipPrimitive.Root, { open: isOpen, onOpenChange: modalityAwareHandleOpenChange, delayDuration: delayDurationMs, children: [sentinel, _jsx(TooltipPrimitive.Trigger, { asChild: true, onPointerEnter: handlePointerEnter, onPointerLeave: handlePointerLeave, onPointerDown: 
                // Prevents focus from shifting away from the tooltip, which would close the tooltip
                shouldStayOpenOnClick ? (event) => event.preventDefault() : undefined, onClick: 
                // Prevents the default click behavior that Radix uses to close the tooltip
                shouldStayOpenOnClick ? (event) => event.preventDefault() : undefined, ...__radixProps, children: triggerChild }), _jsx(TooltipPrimitive.Portal, { container: resolvedPortalTarget, children: _jsx(TooltipPrimitive.Content, { asChild: true, side: side, align: align, sideOffset: offsetPx, avoidCollisions: true, collisionPadding: collisionPadding, hideWhenDetached: hideWhenDetached, onPointerDownOutside: 
                    // Prevents the tooltip from closing when clicking outside of it
                    shouldStayOpenOnClick
                        ? (event) => {
                            // Prevent tooltip dismissal on outside click — keep tooltip open per shouldStayOpenOnClick
                            event.preventDefault();
                        }
                        : undefined, className: tooltipContentVariants({ side }), style: { ...overlayZIndexStyle, maxWidth: `${maxWidthPx}px` }, children: _jsxs(TooltipContentElement, { children: [contentMountSignal, _jsxs("div", { className: "flex flex-col gap-0.5", children: [_jsx("div", { className: "text-xs font-medium text-white break-words", children: content }), subtitle && _jsx("div", { className: "text-xs text-white/50 break-words", children: subtitle })] }), shouldShowArrow && (_jsx(TooltipPrimitive.Arrow, { asChild: true, className: arrowClasses, children: _jsx("div", {}) }))] }) }) })] }));
    if (skipDelayDurationMs === undefined) {
        return tooltipNode;
    }
    return (_jsx(TooltipPrimitive.Provider, { skipDelayDuration: skipDelayDurationMs, children: tooltipNode }));
}
//# sourceMappingURL=index.js.map